import dotPropImmutable from 'dot-prop-immutable';
import moment from 'moment';
import { createIntl } from 'react-intl';
import { IntegrationProvider } from 'api/integrations';
import { DEFAULT_DATE_FORMAT, MemberType } from 'api/models';
import { toastService } from 'components/molecules/toast';
import { PhaseEntityType, converToEntityId } from 'data/projects/phase-entity';
import { getTranslationCache } from 'utils/translation-loader';
import { insert } from '../utils';
const intl = createIntl({
    locale: 'en',
    messages: getTranslationCache().get('en') || {}
});
function deleteSubTasks(subTasks, state) {
    return subTasks ? subTasks.reduce((intermediateState, taskId)=>{
        var _children_subTasks;
        const children = dotPropImmutable.get(intermediateState, `data.${state.activeId}.tasks.${taskId}`);
        // Stale subTask ids (already removed) must not crash the cascade.
        if (!children) {
            return intermediateState;
        }
        let updatedState = intermediateState;
        if (((_children_subTasks = children.subTasks) === null || _children_subTasks === void 0 ? void 0 : _children_subTasks.length) > 0) {
            updatedState = deleteSubTasks(children.subTasks, updatedState);
        }
        updatedState = {
            ...updatedState,
            ...dotPropImmutable.delete(updatedState, `data.${state.activeId}.tasks.${taskId}`)
        };
        return updatedState;
    }, state) : state;
}
export const removeTaskFromDependencies = (taskId, state)=>{
    var _dotPropImmutable_get;
    const tasks = Object.values((_dotPropImmutable_get = dotPropImmutable.get(state, `data.${state.activeId}.tasks`)) !== null && _dotPropImmutable_get !== void 0 ? _dotPropImmutable_get : {});
    return tasks.reduce((_state, task)=>{
        var _task_dependentTasks;
        // Sparse / corrupted task maps can yield undefined entries.
        if (!task || task.taskId === taskId) {
            return _state;
        }
        // Drop null/undefined holes and entries missing taskId — those used to
        // throw "Cannot read properties of undefined (reading 'taskId')" on delete.
        const dependentTasks = (_task_dependentTasks = task.dependentTasks) === null || _task_dependentTasks === void 0 ? void 0 : _task_dependentTasks.filter((depTask)=>(depTask === null || depTask === void 0 ? void 0 : depTask.taskId) != null);
        if (!(dependentTasks === null || dependentTasks === void 0 ? void 0 : dependentTasks.some((depTask)=>depTask.taskId === taskId))) {
            return _state;
        }
        return dotPropImmutable.merge(_state, `data.${state.activeId}.tasks.${task.taskId}`, {
            ...task,
            dependentTasks: dependentTasks.filter((depTask)=>depTask.taskId !== taskId)
        });
    }, state);
};
const isLastTaskValid = (state, taskIds)=>!dotPropImmutable.get(state, `data.${state.activeId}.tasks.${taskIds[taskIds.length - 1]}.isNew`, false);
const getIndexToInsert = (state, taskIds)=>isLastTaskValid(state, taskIds) ? taskIds.length : taskIds.length - 1;
const makeSubTasksAsPrivate = (state, subTasks)=>{
    return subTasks ? subTasks.reduce((intermediateState, taskId)=>{
        var _children_subTasks;
        const children = dotPropImmutable.get(intermediateState, `data.${state.activeId}.tasks.${taskId}`);
        let updatedState = intermediateState;
        if ((children === null || children === void 0 ? void 0 : (_children_subTasks = children.subTasks) === null || _children_subTasks === void 0 ? void 0 : _children_subTasks.length) > 0) {
            updatedState = makeSubTasksAsPrivate(updatedState, children.subTasks);
        }
        return dotPropImmutable.set(updatedState, `data.${state.activeId}.tasks.${taskId}.private`, true);
    }, state) : state;
};
const makeTaskAsPrivate = (state, taskId)=>{
    const result = dotPropImmutable.set(state, `data.${state.activeId}.tasks.${taskId}.private`, true);
    const subTasks = dotPropImmutable.get(result, `data.${state.activeId}.tasks.${taskId}.subTasks`, []);
    return makeSubTasksAsPrivate(result, subTasks);
};
/* @agent:lesson(2026-10-10)
 * A sheet task carries its assignee twice: `task.assignee`, which the plan
 * grid renders, and `task.sheetTemplate.assignee`, which the task detail
 * panel renders and which the server reconciles `task.assignee` from when
 * the template is saved. Ship the two out of sync and the backend drops
 * `task.assignee` outright, so every write of a sheet task's assignee has
 * to go through this helper rather than setting `assignee` on its own.
 */ const mirrorAssigneeIntoSheetTemplate = (task)=>task.sheetTemplate ? {
        ...task,
        sheetTemplate: {
            ...task.sheetTemplate,
            assignee: task.assignee || {
                users: [],
                teams: []
            }
        }
    } : task;
const resetStateForPrivateTask = (state, taskId)=>{
    const responsibleOn = dotPropImmutable.get(state, `data.${state.activeId}.tasks.${taskId}.responsibleOn`);
    const assignee = dotPropImmutable.get(state, `data.${state.activeId}.tasks.${taskId}.assignee`);
    let resetState = dotPropImmutable.set(state, `data.${state.activeId}.tasks.${taskId}.responsibleOn`, responsibleOn === null || responsibleOn === void 0 ? void 0 : responsibleOn.filter((_value)=>_value !== MemberType.External));
    if (assignee && assignee.teams) {
        var _assignee_teams;
        resetState = dotPropImmutable.set(resetState, `data.${state.activeId}.tasks.${taskId}.assignee.teams`, (assignee === null || assignee === void 0 ? void 0 : (_assignee_teams = assignee.teams) === null || _assignee_teams === void 0 ? void 0 : _assignee_teams.filter((_team)=>_team.teamType !== MemberType.External)) || []);
    }
    return resetState;
};
export const taskSlice = {
    createProjectTemplateTask: (state, { payload })=>{
        const { projectPhaseId, task, insertAt } = payload;
        const parentProjectPhase = dotPropImmutable.get(state, `data.${state.activeId}.projectPhases.${projectPhaseId}`, {});
        const taskPayload = {
            ...task,
            projectPhase: {
                projectPhaseId
            },
            subTasks: [],
            startDay: parentProjectPhase.startDay || 1,
            duration: 1,
            private: parentProjectPhase.private,
            isNewEntity: true
        };
        const taskPath = `data.${state.activeId}.tasks.${taskPayload.taskId}`;
        const taskResult = dotPropImmutable.set(state, taskPath, taskPayload);
        const taskRefPath = `data.${state.activeId}.projectPhases.${projectPhaseId}.tasks`;
        const entityOrder = dotPropImmutable.get(taskResult, `data.${state.activeId}.projectPhases.${projectPhaseId}.entityOrder`, []);
        let result;
        if (!insertAt) {
            const taskOrderResult = dotPropImmutable.merge(taskResult, taskRefPath, [
                taskPayload.taskId
            ]);
            result = dotPropImmutable.set(taskOrderResult, `data.${state.activeId}.projectPhases.${projectPhaseId}.entityOrder`, [
                ...entityOrder,
                converToEntityId(task.taskId, PhaseEntityType.Task)
            ]);
        } else {
            const insertAtIndex = entityOrder.findIndex((id)=>id === insertAt.id);
            const taskIds = dotPropImmutable.get(taskResult, taskRefPath, []);
            const modifiedTaskIds = insert(taskIds, insertAtIndex + insertAt.dir, task.taskId);
            const taskOrderResult = dotPropImmutable.set(taskResult, taskRefPath, modifiedTaskIds);
            const modifiedEntityOrder = insert(entityOrder, insertAtIndex + insertAt.dir, converToEntityId(task.taskId, PhaseEntityType.Task));
            result = dotPropImmutable.set(taskOrderResult, `data.${state.activeId}.projectPhases.${projectPhaseId}.entityOrder`, modifiedEntityOrder);
        }
        return result;
    },
    createProjectTemplateSubTask: (state, { payload })=>{
        const { parentTaskId, task, insertAt } = payload;
        const parentTask = dotPropImmutable.get(state, `data.${state.activeId}.tasks.${parentTaskId}`);
        const taskPayload = {
            ...task,
            parentTask: {
                taskId: parentTaskId
            },
            subTasks: [],
            startDay: parentTask.startDay,
            duration: parentTask.duration,
            private: parentTask.private,
            isNewEntity: true
        };
        const taskPath = `data.${state.activeId}.tasks.${task.taskId}`;
        const taskResult = dotPropImmutable.set(state, taskPath, taskPayload);
        const taskRefPath = `data.${state.activeId}.tasks.${parentTaskId}.subTasks`;
        let result;
        if (!insertAt) {
            result = dotPropImmutable.merge(taskResult, taskRefPath, [
                taskPayload.taskId
            ]);
        } else {
            const taskIds = dotPropImmutable.get(taskResult, taskRefPath, []);
            const insertAtIndex = taskIds.findIndex((i)=>i === insertAt.id);
            const modifiedTaskIds = insert(taskIds, insertAtIndex + insertAt.dir, payload.task.taskId);
            result = dotPropImmutable.set(taskResult, taskRefPath, modifiedTaskIds);
        }
        return result;
    },
    updateProjectTemplateTask: (state, { payload: { task, isNew, updatePrivacyOfChildren } })=>{
        const { private: isPrivate } = task;
        const allTasksMap = dotPropImmutable.get(state, `data.${state.activeId}.tasks`);
        const taskWithSameExternalId = Object.values(allTasksMap).filter((e)=>e.externalRefKey && e.externalRefKey === task.externalRefKey && e.taskId !== task.taskId);
        if (taskWithSameExternalId.length > 0) {
            toastService({
                title: intl.formatMessage({
                    id: 'templates.external_ref_key_exists.title'
                }),
                message: intl.formatMessage({
                    id: 'templates.external_ref_key_exists.body'
                }, {
                    externalRefKey: task.externalRefKey
                })
            }).error();
            return state;
        }
        let updatedTask = mirrorAssigneeIntoSheetTemplate(task);
        let result = dotPropImmutable.merge(state, `data.${state.activeId}.tasks.${task.taskId}`, {
            ...updatedTask,
            ...{
                isNew
            }
        });
        if (isPrivate) {
            const resetState = resetStateForPrivateTask(result, task.taskId);
            const resetTask = dotPropImmutable.get(resetState, `data.${state.activeId}.tasks.${task.taskId}`);
            // Rebuild from `updatedTask`, not `task` — `task` still carries the
            // pre-mirror `sheetTemplate`, and the merge below would write it back
            // over the mirrored one. Re-mirror afterwards so the sheet template
            // tracks the post-reset assignee (`resetStateForPrivateTask` strips
            // External teams), keeping the two copies identical.
            updatedTask = mirrorAssigneeIntoSheetTemplate({
                ...updatedTask,
                responsibleOn: resetTask.responsibleOn,
                assignee: resetTask.assignee
            });
        }
        const { subTasks } = task;
        function setPrivate(_subTasks) {
            _subTasks === null || _subTasks === void 0 ? void 0 : _subTasks.forEach((sid)=>{
                const children = dotPropImmutable.get(result, `data.${state.activeId}.tasks.${sid}`);
                result = dotPropImmutable.set(result, `data.${state.activeId}.tasks.${sid}.private`, isPrivate);
                result = isPrivate ? resetStateForPrivateTask(result, sid) : result;
                if (children.subTasks.length > 0) {
                    setPrivate(children.subTasks);
                }
            });
        }
        if (updatePrivacyOfChildren === true) {
            setPrivate(subTasks);
        }
        return dotPropImmutable.merge(result, `data.${state.activeId}.tasks.${task.taskId}`, {
            ...updatedTask,
            ...{
                isNew
            }
        });
    },
    updateProjectTemplateTasks: (state, { payload })=>{
        const updatedTasks = {};
        payload === null || payload === void 0 ? void 0 : payload.forEach(({ id, startDay, duration, effort, taskEffortMeta, taskEffortSetting })=>{
            const task = state.data[`${state.activeId}`].tasks[id];
            const updates = {};
            if (startDay) {
                updates.startDay = startDay;
            }
            if (duration) {
                updates.duration = duration;
            }
            if (effort != null) {
                updates.effort = effort;
            }
            if (taskEffortMeta != null) {
                updates.taskEffortMeta = taskEffortMeta;
            }
            if (taskEffortSetting) {
                updates.taskEffortSetting = taskEffortSetting;
            }
            updatedTasks[id] = {
                ...task,
                ...updates
            };
        });
        return dotPropImmutable.merge(state, `data.${state.activeId}.tasks`, updatedTasks);
    },
    makeDateShiftsForTasks: (state, { payload })=>{
        const updatedTasks = {};
        payload === null || payload === void 0 ? void 0 : payload.forEach((taskUpdates)=>{
            const { taskId, startDate, dueDate, dependentTasks } = taskUpdates;
            const task = state.data[`${state.activeId}`].tasks[taskId];
            const currentStartDate = moment().add(task.startDay, 'day').startOf('day').format(DEFAULT_DATE_FORMAT);
            const currentDueDate = moment().add(task.startDay + task.duration - 1, 'day').endOf('day').format(DEFAULT_DATE_FORMAT);
            updatedTasks[taskId] = {
                ...task,
                startDay: startDate ? Math.round(Math.abs(moment().startOf('day').diff(moment(startDate), 'day', true))) : task.startDay,
                duration: startDate || dueDate ? Math.round(moment(dueDate || currentDueDate).endOf('day').diff(moment(startDate || currentStartDate).startOf('day'), 'day', true)) : task.duration,
                dependentTasks: dependentTasks || task.dependentTasks
            };
        });
        return dotPropImmutable.merge(state, `data.${state.activeId}.tasks`, updatedTasks);
    },
    updateProjectTemplateParentOfTask: (state, { payload })=>{
        let result;
        if (payload.projectPhase) {
            const parentPhase = dotPropImmutable.get(state, `data.${state.activeId}.projectPhases.${payload.projectPhase.projectPhaseId}`, {});
            /**
       * Update parent reference in the task.
       */ result = dotPropImmutable.merge(state, `data.${state.activeId}.tasks.${payload.task.taskId}`, {
                ...payload.task,
                projectPhase: payload.projectPhase,
                parentTask: undefined
            });
            if (parentPhase.private === true) {
                result = makeTaskAsPrivate(result, payload.task.taskId);
            }
            /**
       * Remove task reference from existing parent (either phase or parent task).
       */ let taskRefPath = `data.${state.activeId}.projectPhases.${payload.projectPhase.projectPhaseId}.tasks`;
            let taskIds = dotPropImmutable.get(result, taskRefPath, []);
            if (payload.task.projectPhase) {
                taskRefPath = `data.${state.activeId}.projectPhases.${payload.task.projectPhase.projectPhaseId}.tasks`;
                taskIds = dotPropImmutable.get(result, taskRefPath, []).filter((id)=>id !== payload.task.taskId);
                result = dotPropImmutable.set(result, taskRefPath, taskIds);
            } else if (payload.task.parentTask) {
                taskRefPath = `data.${state.activeId}.tasks.${payload.task.parentTask.taskId}.subTasks`;
                taskIds = dotPropImmutable.get(result, taskRefPath, []).filter((id)=>id !== payload.task.taskId);
                result = dotPropImmutable.set(result, taskRefPath, taskIds);
            }
            /**
       * Add task reference to the new parent.
       */ taskIds = dotPropImmutable.get(result, `data.${state.activeId}.projectPhases.${payload.projectPhase.projectPhaseId}.tasks`, []);
            const indexToAdd = payload.insertAt == null ? getIndexToInsert(result, taskIds) : payload.insertAt;
            result = dotPropImmutable.set(result, `data.${state.activeId}.projectPhases.${payload.projectPhase.projectPhaseId}.tasks`, insert(taskIds, indexToAdd, payload.task.taskId));
        } else if (payload.parentTask) {
            const parentTask = dotPropImmutable.get(state, `data.${state.activeId}.tasks.${payload.parentTask.taskId}`, {});
            result = dotPropImmutable.merge(state, `data.${state.activeId}.tasks.${payload.task.taskId}`, {
                ...payload.task,
                parentTask: payload.parentTask,
                projectPhase: undefined
            });
            if (parentTask.private === true) {
                result = makeTaskAsPrivate(result, payload.task.taskId);
            }
            let taskRefPath = `data.${state.activeId}.tasks.${payload.parentTask.taskId}.subTasks`;
            let taskIds = dotPropImmutable.get(result, taskRefPath, []);
            if (payload.task.parentTask) {
                taskRefPath = `data.${state.activeId}.tasks.${payload.task.parentTask.taskId}.subTasks`;
                taskIds = dotPropImmutable.get(result, taskRefPath, []).filter((id)=>id !== payload.task.taskId);
                result = dotPropImmutable.set(result, taskRefPath, taskIds);
            } else if (payload.task.projectPhase) {
                taskRefPath = `data.${state.activeId}.projectPhases.${payload.task.projectPhase.projectPhaseId}.tasks`;
                taskIds = dotPropImmutable.get(result, taskRefPath, []).filter((id)=>id !== payload.task.taskId);
                result = dotPropImmutable.set(result, taskRefPath, taskIds);
            }
            taskIds = dotPropImmutable.get(result, `data.${state.activeId}.tasks.${payload.parentTask.taskId}.subTasks`, []);
            const indexToAdd = payload.insertAt == null ? getIndexToInsert(result, taskIds) : payload.insertAt;
            result = dotPropImmutable.set(result, `data.${state.activeId}.tasks.${payload.parentTask.taskId}.subTasks`, insert(taskIds, indexToAdd, payload.task.taskId));
        } else {
            return state;
        }
        return result;
    },
    deleteProjectTemplateTask: (state, { payload })=>{
        var _taskResult_projectPhase, _result_data_state_activeId_projectPhases, _result_data_state_activeId;
        const path = `data.${state.activeId}.tasks.${payload.taskId}`;
        const taskResult = dotPropImmutable.get(state, path);
        // Missing task (stale id / race) — no-op instead of crashing the saga.
        if (!(taskResult === null || taskResult === void 0 ? void 0 : (_taskResult_projectPhase = taskResult.projectPhase) === null || _taskResult_projectPhase === void 0 ? void 0 : _taskResult_projectPhase.projectPhaseId)) {
            return state;
        }
        const { subTasks } = taskResult;
        let result = deleteSubTasks(subTasks, state);
        result = {
            ...result,
            ...dotPropImmutable.delete(removeTaskFromDependencies(payload.taskId, result), path)
        };
        const phase = (_result_data_state_activeId = result.data[state.activeId]) === null || _result_data_state_activeId === void 0 ? void 0 : (_result_data_state_activeId_projectPhases = _result_data_state_activeId.projectPhases) === null || _result_data_state_activeId_projectPhases === void 0 ? void 0 : _result_data_state_activeId_projectPhases[taskResult.projectPhase.projectPhaseId];
        if (!(phase === null || phase === void 0 ? void 0 : phase.tasks)) {
            return result;
        }
        const taskIds = phase.tasks.filter((id)=>id !== payload.taskId);
        result = dotPropImmutable.set(result, `data.${state.activeId}.projectPhases.${taskResult.projectPhase.projectPhaseId}.tasks`, taskIds);
        return result;
    },
    deleteProjectTemplateSubTask: (state, { payload })=>{
        var _taskResult_parentTask, _result_data_state_activeId_tasks, _result_data_state_activeId;
        const path = `data.${state.activeId}.tasks.${payload.taskId}`;
        const taskResult = dotPropImmutable.get(state, path);
        // Missing task or parent — no-op. Calling parentTask.taskId on a
        // top-level task misrouted here used to throw reading 'taskId'.
        if (!(taskResult === null || taskResult === void 0 ? void 0 : (_taskResult_parentTask = taskResult.parentTask) === null || _taskResult_parentTask === void 0 ? void 0 : _taskResult_parentTask.taskId)) {
            return state;
        }
        const { subTasks } = taskResult;
        let result = deleteSubTasks(subTasks, state);
        result = {
            ...result,
            ...dotPropImmutable.delete(removeTaskFromDependencies(payload.taskId, result), path)
        };
        const parent = (_result_data_state_activeId = result.data[state.activeId]) === null || _result_data_state_activeId === void 0 ? void 0 : (_result_data_state_activeId_tasks = _result_data_state_activeId.tasks) === null || _result_data_state_activeId_tasks === void 0 ? void 0 : _result_data_state_activeId_tasks[taskResult.parentTask.taskId];
        if (!(parent === null || parent === void 0 ? void 0 : parent.subTasks)) {
            return {
                ...state,
                ...result
            };
        }
        const taskIds = parent.subTasks.filter((id)=>id !== payload.taskId);
        result = dotPropImmutable.set(result, `data.${state.activeId}.tasks.${taskResult.parentTask.taskId}.subTasks`, taskIds);
        return {
            ...state,
            ...result
        };
    },
    addProjectTemplateTaskDependency: (state, { payload: { taskId, dependentTaskId, dependencyType, lag } })=>{
        const dependentTask = dotPropImmutable.get(state, `data.${state.activeId}.tasks.${dependentTaskId}`);
        // Do not record a dependency on a missing task — that used to push
        // `{ taskId: undefined }` into dependentTasks and later crash deletes.
        if (!(dependentTask === null || dependentTask === void 0 ? void 0 : dependentTask.taskId)) {
            return state;
        }
        return dotPropImmutable.merge(state, `data.${state.activeId}.tasks.${taskId}.dependentTasks`, [
            {
                taskId: dependentTask.taskId,
                taskName: dependentTask.taskName,
                dependencyType,
                lag
            }
        ]);
    },
    updateProjectTemplateTaskDependencies: (state, { payload: { taskId, dependentTasks } })=>{
        return dotPropImmutable.set(state, `data.${state.activeId}.tasks.${taskId}.dependentTasks`, dependentTasks);
    },
    addProjectTemplatePhaseDependency: (state, { payload: { phaseId, dependentPhaseId, dependencyType, lag } })=>{
        const dependentPhase = dotPropImmutable.get(state, `data.${state.activeId}.projectPhases.${dependentPhaseId}`, {});
        return dotPropImmutable.merge(state, `data.${state.activeId}.projectPhases.${phaseId}.dependentProjectPhases`, [
            {
                projectPhaseId: dependentPhase.projectPhaseId,
                projectPhaseName: dependentPhase.projectPhaseName,
                dependencyType,
                lag
            }
        ]);
    },
    updateProjectTemplatePhaseDependencies: (state, { payload: { phaseId, dependentPhases } })=>{
        return dotPropImmutable.set(state, `data.${state.activeId}.projectPhases.${phaseId}.dependentProjectPhases`, dependentPhases);
    },
    updateJiraMappingInTemplate: (state, { payload })=>{
        const integrationResource = (payload === null || payload === void 0 ? void 0 : payload.data) ? [
            {
                externalIntegrationType: IntegrationProvider.Jira,
                externalResourceType: 'issue',
                externalResourceInputs: payload.data
            }
        ] : [];
        return dotPropImmutable.set(state, `data.${state.activeId}.tasks.${payload.taskId}.linkedResources`, integrationResource);
    }
};
